const request = async (authorization, url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      Accept: 'application/json',
      Authorization: authorization,
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });
  const payload = await response.json().catch(() => ({ error: 'El backend devolvio una respuesta invalida.' }));
  if (!response.ok) {
    const error = new Error(payload.error || `El backend respondio ${response.status}.`);
    error.status = response.status;
    throw error;
  }
  return payload;
};

export const createBasicAuthorization = (username, password) => (
  `Basic ${window.btoa(Array.from(
    new TextEncoder().encode(`${username}:${password}`),
    (byte) => String.fromCharCode(byte),
  ).join(''))}`
);

const GAME_MENU_URL = '/api/admin/config/game-menu';
const HIDDEN_POINTS_URL = '/api/admin/config/hidden-points';
const HIDDEN_POINTS_HONEYPOT_URL = '/api/admin/hidden-points/honeypot';
const HIDDEN_POINTS_SIGNALS_URL = '/api/admin/hidden-points/signals';
const LOCATION_GPS_URL = '/api/admin/config/location-gps';
const MAIL_URL = '/api/admin/config/mail';
const MAIL_TEST_URL = '/api/admin/config/mail/test';
const EVENT_SUBSCRIBERS_URL = '/api/admin/events/subscribers';
const RANKING_OVERVIEW_URL = '/api/admin/ranking/overview';
const RANKING_RESET_URL = '/api/admin/ranking/reset';
const SCORING_URL = '/api/admin/config/scoring';

export const fetchAdminGameMenuConfig = (authorization) => request(authorization, GAME_MENU_URL);

export const saveAdminGameMenuConfig = (authorization, config) => request(authorization, GAME_MENU_URL, {
  method: 'PUT',
  body: JSON.stringify({ config }),
});

export const resetAdminGameMenuConfig = (authorization) => request(authorization, GAME_MENU_URL, {
  method: 'DELETE',
});

export const fetchAdminHiddenPointsConfig = (authorization) => request(authorization, HIDDEN_POINTS_URL);
export const fetchAdminHiddenPointsHoneypot = (authorization) => request(authorization, HIDDEN_POINTS_HONEYPOT_URL);
export const fetchAdminHiddenPointsSignals = (authorization) => request(authorization, HIDDEN_POINTS_SIGNALS_URL);

export const saveAdminHiddenPointsConfig = (authorization, config) => request(authorization, HIDDEN_POINTS_URL, {
  method: 'PUT',
  body: JSON.stringify({ config }),
});

export const resetAdminHiddenPointsConfig = (authorization) => request(authorization, HIDDEN_POINTS_URL, {
  method: 'DELETE',
});

export const fetchAdminLocationGpsConfig = (authorization) => request(authorization, LOCATION_GPS_URL);

export const saveAdminLocationGpsConfig = (authorization, config) => request(authorization, LOCATION_GPS_URL, {
  method: 'PUT',
  body: JSON.stringify({ config }),
});

export const resetAdminLocationGpsConfig = (authorization) => request(authorization, LOCATION_GPS_URL, {
  method: 'DELETE',
});

export const fetchAdminMailConfig = (authorization) => request(authorization, MAIL_URL);

export const saveAdminMailConfig = (authorization, config) => request(authorization, MAIL_URL, {
  method: 'PUT',
  body: JSON.stringify({ config }),
});

export const resetAdminMailConfig = (authorization) => request(authorization, MAIL_URL, {
  method: 'DELETE',
});

export const testAdminMailConfig = (authorization, to, config) => request(authorization, MAIL_TEST_URL, {
  method: 'POST',
  body: JSON.stringify(config ? { to, config } : { to }),
});

export const fetchAdminEventSubscribers = (authorization, { limit = 100, offset = 0 } = {}) => (
  request(authorization, `${EVENT_SUBSCRIBERS_URL}?limit=${encodeURIComponent(limit)}&offset=${encodeURIComponent(offset)}`)
);

export const fetchAdminRankingOverview = (authorization) => request(authorization, RANKING_OVERVIEW_URL);

export const resetAdminRanking = (authorization, note = '') => request(authorization, RANKING_RESET_URL, {
  method: 'POST',
  body: JSON.stringify({ note }),
});

const TOURNAMENTS_URL = '/api/admin/tournaments';
const TOURNAMENTS_CLOSE_URL = '/api/admin/tournaments/close';
const TOURNAMENTS_DISPLAY_URL = '/api/admin/tournaments/display';

export const fetchAdminTournaments = (authorization) => request(authorization, TOURNAMENTS_URL);

export const fetchAdminTournamentRanking = (authorization, tournamentId, { limit = 50, offset = 0 } = {}) => (
  request(authorization, `${TOURNAMENTS_URL}/ranking?tournamentId=${encodeURIComponent(tournamentId)}`
    + `&limit=${encodeURIComponent(limit)}&offset=${encodeURIComponent(offset)}`)
);

export const createAdminTournament = (authorization, tournament) => request(authorization, TOURNAMENTS_URL, {
  method: 'POST',
  body: JSON.stringify(tournament),
});

export const closeAdminTournament = (authorization, id) => request(authorization, TOURNAMENTS_CLOSE_URL, {
  method: 'POST',
  body: JSON.stringify({ id }),
});

export const setAdminTournamentDisplay = (authorization, id, display) => request(authorization, TOURNAMENTS_DISPLAY_URL, {
  method: 'POST',
  body: JSON.stringify({ id, ...display }),
});

export const fetchAdminScoringConfig = (authorization) => request(authorization, SCORING_URL);
export const saveAdminScoringConfig = (authorization, config) => request(authorization, SCORING_URL, {
  method: 'PUT', body: JSON.stringify({ config }),
});
export const resetAdminScoringConfig = (authorization) => request(authorization, SCORING_URL, { method: 'DELETE' });

const FESTIVAL_CLOSURE_URL = '/api/admin/config/festival';

export const fetchAdminFestivalClosure = (authorization) => request(authorization, FESTIVAL_CLOSURE_URL);

export const saveAdminFestivalClosure = (authorization, config) => request(authorization, FESTIVAL_CLOSURE_URL, {
  method: 'PUT',
  body: JSON.stringify({ config }),
});

const CLOSURE_INVITATIONS_URL = '/api/admin/closure-invitations';

export const fetchAdminClosureInvitations = (authorization) => request(authorization, CLOSURE_INVITATIONS_URL);

export const testAdminClosureInvitation = (authorization, { kind, to, details }) => (
  request(authorization, `${CLOSURE_INVITATIONS_URL}/test`, { method: 'POST', body: JSON.stringify({ kind, to, details }) })
);

export const sendAdminClosureInvitations = (authorization, { kind, details }) => (
  request(authorization, `${CLOSURE_INVITATIONS_URL}/send`, {
    method: 'POST', body: JSON.stringify({ kind, details, confirm: true }),
  })
);

// CSV de suscritos (opt-in de eventos) listo para importar en Mailchimp o WordPress.
export async function downloadAdminEventSubscribersCsv(authorization) {
  const response = await fetch('/api/admin/events/subscribers.csv', { headers: { Authorization: authorization } });
  if (!response.ok) {
    const error = new Error(`El backend respondio ${response.status}.`);
    error.status = response.status;
    throw error;
  }
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = 'nacanaca-suscritos.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
